import { useState } from 'react';
import { Archive, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface ArchiveProducerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  producerId: string;
  producerName: string;
  isArchived: boolean;
  onSuccess?: () => void;
}

export function ArchiveProducerDialog({
  open,
  onOpenChange,
  producerId,
  producerName,
  isArchived,
  onSuccess,
}: ArchiveProducerDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleArchiveToggle = async () => {
    setIsSubmitting(true);
    try {
      const newStatus = isArchived ? 'draft' : 'archived';
      const { error } = await supabase
        .from('producers')
        .update({ status: newStatus })
        .eq('id', producerId);

      if (error) throw error;

      toast({
        title: isArchived ? 'Gården är återaktiverad' : 'Gården är arkiverad',
        description: isArchived
          ? 'Gården är nu synlig som utkast.'
          : 'Gården är nu dold för besökare.',
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      toast({
        title: 'Något gick fel',
        description: err.message || 'Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            {isArchived ? (
              <RotateCcw className="h-5 w-5 text-primary" />
            ) : (
              <Archive className="h-5 w-5 text-muted-foreground" />
            )}
            <AlertDialogTitle>
              {isArchived ? 'Återaktivera gård?' : 'Arkivera gård?'}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            {isArchived ? (
              <>
                <strong>{producerName}</strong> kommer att bli synlig igen som utkast. 
                Du kan sedan publicera den när du är redo.
              </>
            ) : (
              <>
                <strong>{producerName}</strong> blir osynlig för besökare. 
                Du kan återaktivera gården när som helst.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Avbryt</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleArchiveToggle}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Vänta...'
              : isArchived
              ? 'Återaktivera'
              : 'Arkivera'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
